// pages/QuoteDetail.jsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Edit,
  Trash2,
  MoreVertical,
  FileText,
  Activity as ActivityIcon,
  DollarSign,
  Plus,
  CheckCircle,
  Target,
  AlertCircle,
  Copy,
  Download,
  Calendar,
  User,
  Building2,
  Mail,
  Phone,
  Send,
  Save,
  X as CloseIcon,
  Loader,
  ExternalLink,
  XCircle,
  Eye,
  Clock,
  Package,
  MapPin,
  Link as LinkIcon,
  Globe,
} from "lucide-react";
import apiService from "../../services/api";
import { useTheme } from "../../contexts/ThemeContext";

const STATUS_CONFIG = {
  draft: { label: "Draft", color: "gray", icon: FileText },
  sent: { label: "Sent", color: "blue", icon: Send },
  viewed: { label: "Viewed", color: "yellow", icon: Eye },
  signed: { label: "Signed", color: "green", icon: CheckCircle },
  accepted: { label: "Accepted", color: "green", icon: CheckCircle },
  rejected: { label: "Rejected", color: "red", icon: XCircle },
  expired: { label: "Expired", color: "gray", icon: Clock },
  cancelled: { label: "Cancelled", color: "red", icon: XCircle },
};

const QuoteDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();

  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("details");
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);

  // Inline editing
  const [editingField, setEditingField] = useState(null);
  const [editingValue, setEditingValue] = useState("");
  const [savingField, setSavingField] = useState(false);

  // Confirmation
  const [showSendConfirmation, setShowSendConfirmation] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  useEffect(() => {
    fetchQuote();
  }, [id]);

  const fetchQuote = async () => {
    try {
      setLoading(true);
      const response = await apiService.getDigitalQuote(id);
      if (response.success) {
        setQuote(response.data);
      }
    } catch (error) {
      console.error("Error fetching quote:", error);
      navigate("/quotes");
    } finally {
      setLoading(false);
    }
  };

  const handleSendQuote = async () => {
    try {
      setSending(true);
      const response = await apiService.sendDigitalQuote(id);
      if (response.success) {
        setShowSendConfirmation(false);
        await fetchQuote();
      }
    } catch (error) {
      console.error("Error sending quote:", error);
      alert("Failed to send quote");
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async () => {
    try {
      await apiService.deleteDigitalQuote(id);
      navigate("/quotes");
    } catch (error) {
      console.error("Error deleting quote:", error);
      alert("Failed to delete quote");
    }
  };

  const handleCopyPublicLink = () => {
    const url = `${window.location.origin}/quote/${quote.publicToken}`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const startEditing = (field, currentValue) => {
    setEditingField(field);
    if (field.includes("Date") || field === "validUntil") {
      if (currentValue) {
        try {
          const date = new Date(currentValue);
          setEditingValue(date.toISOString().split("T")[0]);
        } catch {
          setEditingValue(currentValue || "");
        }
      } else {
        setEditingValue("");
      }
    } else {
      setEditingValue(currentValue || "");
    }
  };

  const cancelEditing = () => {
    setEditingField(null);
    setEditingValue("");
  };

  const saveFieldEdit = async (field) => {
    try {
      setSavingField(true);
      const updateData = { [field]: editingValue || null };
      await apiService.updateDigitalQuote(id, updateData);
      await fetchQuote();
      setEditingField(null);
      setEditingValue("");
    } catch (error) {
      console.error("Error saving field:", error);
      alert(error.response?.data?.message || "Failed to save changes");
    } finally {
      setSavingField(false);
    }
  };

  const formatCurrency = (amount, currency = "GBP") => {
    if (amount === null || amount === undefined) return "—";
    try {
      return new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: currency || "GBP",
        minimumFractionDigits: 2,
      }).format(amount);
    } catch {
      return `${currency || "GBP"} ${parseFloat(amount).toFixed(2)}`;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return "—";
    }
  };

  const getStatusBadge = (status) => {
    const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
    const Icon = cfg.icon;
    const colorClasses = {
      gray: "text-gray-400 bg-gray-400/10 border-gray-400/20",
      blue: "text-blue-400 bg-blue-400/10 border-blue-400/20",
      yellow: "text-yellow-400 bg-yellow-400/10 border-yellow-400/20",
      green: "text-green-400 bg-green-400/10 border-green-400/20",
      red: "text-red-400 bg-red-400/10 border-red-400/20",
    };
    return (
      <span
        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-lg border text-sm font-medium ${
          colorClasses[cfg.color]
        }`}
      >
        <Icon size={14} />
        {cfg.label}
      </span>
    );
  };

  const EditableField = ({
    label,
    field,
    value,
    type = "text",
    options = null,
    readOnly = false,
  }) => {
    const isEditing = editingField === field;
    const inputRef = React.useRef(null);

    React.useEffect(() => {
      if (isEditing && inputRef.current) {
        const input = inputRef.current;
        if (!options && (type === "text" || type === "textarea")) {
          const length = input.value.length;
          input.setSelectionRange(length, length);
        }
        input.focus();
      }
    }, [isEditing, type, options]);

    if (readOnly) {
      return (
        <div>
          <label
            className={`text-sm ${
              isDarkMode ? "text-gray-400" : "text-gray-500"
            } block mb-1`}
          >
            {label}
          </label>
          <p className={isDarkMode ? "text-white" : "text-gray-900"}>
            {value || "—"}
          </p>
        </div>
      );
    }

    return (
      <div className="group">
        <label
          className={`text-sm ${
            isDarkMode ? "text-gray-400" : "text-gray-500"
          } block mb-1`}
        >
          {label}
        </label>
        <div className="flex items-center gap-2">
          {isEditing ? (
            <div className="flex-1 flex items-center gap-2">
              {options ? (
                <select
                  ref={inputRef}
                  value={editingValue}
                  onChange={(e) => setEditingValue(e.target.value)}
                  className={`flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 ${
                    isDarkMode
                      ? "bg-gray-800 border-gray-700 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  <option value="">Select...</option>
                  {options.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              ) : type === "textarea" ? (
                <textarea
                  ref={inputRef}
                  value={editingValue}
                  onChange={(e) => setEditingValue(e.target.value)}
                  rows={3}
                  className={`flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 resize-none ${
                    isDarkMode
                      ? "bg-gray-800 border-gray-700 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              ) : (
                <input
                  ref={inputRef}
                  type={type}
                  value={editingValue}
                  onChange={(e) => setEditingValue(e.target.value)}
                  className={`flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 ${
                    isDarkMode
                      ? "bg-gray-800 border-gray-700 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              )}
              <button
                onClick={() => saveFieldEdit(field)}
                disabled={savingField}
                className="p-2 bg-green-600 hover:bg-green-700 rounded-lg transition-colors disabled:opacity-50"
              >
                {savingField ? (
                  <Loader size={16} className="animate-spin" />
                ) : (
                  <Save size={16} />
                )}
              </button>
              <button
                onClick={cancelEditing}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode
                    ? "bg-gray-700 hover:bg-gray-600"
                    : "bg-gray-200 hover:bg-gray-300"
                }`}
              >
                <CloseIcon size={16} />
              </button>
            </div>
          ) : (
            <>
              <p
                className={`flex-1 ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                {value || "—"}
              </p>
              <button
                onClick={() => {
                  let rawValue = value;
                  if (type === "date" && value && value !== "—") {
                    rawValue = quote[field];
                  }
                  startEditing(field, rawValue);
                }}
                className={`opacity-0 group-hover:opacity-100 p-1 rounded transition-all ${
                  isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"
                }`}
              >
                <Edit
                  size={14}
                  className={isDarkMode ? "text-gray-400" : "text-gray-500"}
                />
              </button>
            </>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div
        className={`min-h-screen ${
          isDarkMode ? "bg-[#0C0E12]" : "bg-gray-50"
        } flex items-center justify-center`}
      >
        <Loader
          className={`${
            isDarkMode ? "text-white" : "text-gray-900"
          } animate-spin`}
          size={32}
        />
      </div>
    );
  }

  if (!quote) {
    return (
      <div
        className={`min-h-screen ${
          isDarkMode ? "bg-[#0C0E12]" : "bg-gray-50"
        } flex items-center justify-center`}
      >
        <div className={isDarkMode ? "text-white" : "text-gray-900"}>
          Quote not found
        </div>
      </div>
    );
  }

  const publicUrl = `${window.location.origin}/quote/${quote.publicToken}`;
  const isLocked = ["signed", "accepted"].includes(quote.status);

  return (
    <div
      className={`min-h-screen transition-colors duration-300 ${
        isDarkMode ? "bg-[#0C0E12] text-white" : "bg-gray-50 text-gray-900"
      }`}
    >
      {/* Send Confirmation Modal */}
      {showSendConfirmation && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div
            className={`rounded-lg border max-w-md w-full p-6 ${
              isDarkMode
                ? "bg-[#13161B] border-gray-700"
                : "bg-white border-gray-200"
            }`}
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-blue-500/10 flex items-center justify-center">
                <Send className="text-blue-400" size={24} />
              </div>
              <div className="flex-1">
                <h3
                  className={`text-lg font-semibold mb-2 ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  Send Quote to Customer
                </h3>
                <p
                  className={`text-sm ${
                    isDarkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  An email with a secure signing link will be sent to{" "}
                  <strong>{quote.customerEmail}</strong>.
                </p>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowSendConfirmation(false)}
                disabled={sending}
                className={`flex-1 px-4 py-2 rounded-lg transition-colors disabled:opacity-50 ${
                  isDarkMode
                    ? "bg-gray-700 hover:bg-gray-600"
                    : "bg-gray-200 hover:bg-gray-300"
                }`}
              >
                Cancel
              </button>
              <button
                onClick={handleSendQuote}
                disabled={sending}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {sending ? (
                  <>
                    <Loader size={16} className="animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send size={16} />
                    Send Quote
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {showDeleteConfirmation && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div
            className={`rounded-lg border max-w-md w-full p-6 ${
              isDarkMode
                ? "bg-[#13161B] border-gray-700"
                : "bg-white border-gray-200"
            }`}
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center">
                <AlertCircle className="text-red-400" size={24} />
              </div>
              <div className="flex-1">
                <h3
                  className={`text-lg font-semibold mb-2 ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  Delete this quote?
                </h3>
                <p
                  className={`text-sm ${
                    isDarkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  This cannot be undone. Quote{" "}
                  <strong>{quote.quoteNumber}</strong> will be permanently
                  removed.
                </p>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowDeleteConfirmation(false)}
                className={`flex-1 px-4 py-2 rounded-lg transition-colors ${
                  isDarkMode
                    ? "bg-gray-700 hover:bg-gray-600"
                    : "bg-gray-200 hover:bg-gray-300"
                }`}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                <Trash2 size={16} />
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div
        className={`border-b transition-colors ${
          isDarkMode
            ? "bg-[#13161B] border-gray-700"
            : "bg-white border-gray-200"
        }`}
      >
        <div className="max-w-[1600px] mx-auto px-3 sm:px-6 py-4">
          <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
            <button
              onClick={() => navigate("/quotes")}
              className={`flex items-center gap-2 transition-colors ${
                isDarkMode
                  ? "text-gray-400 hover:text-white"
                  : "text-gray-500 hover:text-gray-900"
              }`}
            >
              <ArrowLeft size={20} />
              <span>Back to Quotes</span>
            </button>

            <div className="flex items-center gap-2 flex-wrap justify-end">
              <button
                onClick={() => window.open(publicUrl, "_blank")}
                className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg transition-colors ${
                  isDarkMode
                    ? "bg-gray-700 hover:bg-gray-600"
                    : "bg-gray-200 hover:bg-gray-300"
                }`}
              >
                <Eye size={16} />
                <span className="hidden sm:inline">Preview</span>
              </button>

              <button
                onClick={handleCopyPublicLink}
                className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg transition-colors ${
                  isDarkMode
                    ? "bg-gray-700 hover:bg-gray-600"
                    : "bg-gray-200 hover:bg-gray-300"
                }`}
              >
                {copied ? (
                  <>
                    <CheckCircle size={16} className="text-green-400" />
                    <span className="hidden sm:inline">Copied!</span>
                  </>
                ) : (
                  <>
                    <LinkIcon size={16} />
                    <span className="hidden sm:inline">Copy Link</span>
                  </>
                )}
              </button>

              {!isLocked && (
                <button
                  onClick={() => setShowSendConfirmation(true)}
                  className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
                >
                  <Send size={16} />
                  <span className="hidden sm:inline">
                    {quote.status === "draft" ? "Send Quote" : "Resend"}
                  </span>
                </button>
              )}

              <div className="relative">
                <button
                  onClick={() => setShowActionsMenu(!showActionsMenu)}
                  className={`p-2 rounded-lg transition-colors ${
                    isDarkMode
                      ? "bg-gray-700 hover:bg-gray-600"
                      : "bg-gray-200 hover:bg-gray-300"
                  }`}
                >
                  <MoreVertical size={20} />
                </button>

                {showActionsMenu && (
                  <div
                    className={`absolute right-0 top-12 w-48 border rounded-lg shadow-xl z-50 ${
                      isDarkMode
                        ? "bg-[#13161B] border-gray-700"
                        : "bg-white border-gray-200"
                    }`}
                  >
                    <button
                      onClick={() => {
                        window.print();
                        setShowActionsMenu(false);
                      }}
                      className={`w-full px-4 py-2 text-left flex items-center gap-2 ${
                        isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"
                      }`}
                    >
                      <Download size={16} />
                      Export / Print
                    </button>
                    {!isLocked && (
                      <>
                        <div
                          className={`border-t my-1 ${
                            isDarkMode ? "border-gray-700" : "border-gray-200"
                          }`}
                        />
                        <button
                          onClick={() => {
                            setShowDeleteConfirmation(true);
                            setShowActionsMenu(false);
                          }}
                          className="w-full px-4 py-2 text-left flex items-center gap-2 text-red-400 hover:bg-red-500/10"
                        >
                          <Trash2 size={16} />
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Quote Header Info */}
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2 flex-wrap">
                <div className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center text-white font-semibold text-lg flex-shrink-0">
                  <FileText size={24} />
                </div>
                <div>
                  <h1
                    className={`text-xl sm:text-3xl font-bold ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    {quote.title}
                  </h1>
                  <div
                    className={`text-sm font-mono ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    {quote.quoteNumber}
                  </div>
                </div>

                {getStatusBadge(quote.status)}
              </div>

              <div
                className={`flex flex-wrap items-center gap-4 ${
                  isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                <div className="flex items-center gap-2">
                  <DollarSign size={16} />
                  <span className="font-semibold text-green-400">
                    {formatCurrency(quote.totalAmount, quote.currency)}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Eye size={16} />
                  <span>{quote.viewCount || 0} views</span>
                </div>
                {quote.validUntil && (
                  <div className="flex items-center gap-2">
                    <Calendar size={16} />
                    <span>Valid until {formatDate(quote.validUntil)}</span>
                  </div>
                )}
                {parseFloat(quote.monthlyRecurring) > 0 && (
                  <div className="flex items-center gap-2">
                    <Target size={16} />
                    <span>
                      {formatCurrency(quote.monthlyRecurring, quote.currency)}
                      /month
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Creator & Customer */}
            <div className="flex flex-row sm:flex-col gap-3 flex-wrap">
              {quote.creator && (
                <div
                  className={`flex items-center gap-3 rounded-lg p-3 ${
                    isDarkMode ? "bg-gray-800/50" : "bg-gray-100"
                  }`}
                >
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      isDarkMode ? "bg-gray-700" : "bg-gray-300"
                    }`}
                  >
                    {quote.creator.profileImageUrl ? (
                      <img
                        src={quote.creator.profileImageUrl}
                        alt={quote.creator.name}
                        className="w-full h-full rounded-full object-cover"
                      />
                    ) : (
                      <span
                        className={`text-lg font-medium ${
                          isDarkMode ? "text-white" : "text-gray-700"
                        }`}
                      >
                        {quote.creator.name?.charAt(0).toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div>
                    <div
                      className={`text-xs ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      Created by
                    </div>
                    <div
                      className={`font-medium ${
                        isDarkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      {quote.creator.name}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Signed banner */}
          {quote.signature && (
            <div className="mt-4 bg-gradient-to-br from-green-500/10 to-green-600/5 border border-green-500/30 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <CheckCircle size={16} className="text-green-400" />
                </div>
                <div className="flex-1">
                  <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Digitally Signed
                  </p>
                  <p className="text-sm text-green-400 font-medium mt-0.5">
                    {quote.signature.signerName}
                    {quote.signature.signerTitle &&
                      ` — ${quote.signature.signerTitle}`}{" "}
                    signed on {new Date(quote.signedAt).toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Opportunity link */}
          {quote.opportunity && (
            <div className="mt-4 bg-gradient-to-br from-blue-500/10 to-blue-600/5 border border-blue-500/30 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <Target size={16} className="text-blue-400" />
                </div>
                <div className="flex-1">
                  <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Linked Opportunity
                  </p>
                  <button
                    onClick={() =>
                      navigate(`/opportunities/${quote.opportunity.id}`)
                    }
                    className="text-sm text-blue-400 hover:text-blue-300 font-medium mt-0.5 flex items-center gap-1"
                  >
                    {quote.opportunity.name}
                    <ExternalLink size={12} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1600px] mx-auto px-3 sm:px-6 py-6">
        {/* Customer Info Banner */}
        <div
          className={`rounded-lg border p-4 mb-6 ${
            isDarkMode
              ? "bg-[#13161B] border-gray-700"
              : "bg-white border-gray-200"
          }`}
        >
          <h3
            className={`text-sm font-medium mb-3 ${
              isDarkMode ? "text-gray-400" : "text-gray-500"
            }`}
          >
            Customer Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label
                className={`text-xs block mb-1 ${
                  isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                Name
              </label>
              <p
                className={`text-sm font-medium ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                {quote.customerName}
              </p>
            </div>
            <div>
              <label
                className={`text-xs block mb-1 ${
                  isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                Company
              </label>
              <p
                className={
                  isDarkMode ? "text-white text-sm" : "text-gray-900 text-sm"
                }
              >
                {quote.customerCompany || "—"}
              </p>
            </div>
            <div>
              <label
                className={`text-xs block mb-1 ${
                  isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                Email
              </label>
              <a
                href={`mailto:${quote.customerEmail}`}
                className="text-blue-400 hover:text-blue-300 text-sm"
              >
                {quote.customerEmail}
              </a>
            </div>
            <div>
              <label
                className={`text-xs block mb-1 ${
                  isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                Phone
              </label>
              {quote.customerPhone ? (
                <a
                  href={`tel:${quote.customerPhone}`}
                  className="text-blue-400 hover:text-blue-300 text-sm"
                >
                  {quote.customerPhone}
                </a>
              ) : (
                <p
                  className={
                    isDarkMode
                      ? "text-gray-400 text-sm"
                      : "text-gray-500 text-sm"
                  }
                >
                  —
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Tabs */}
            <div
              className={`rounded-lg border ${
                isDarkMode
                  ? "bg-[#13161B] border-gray-700"
                  : "bg-white border-gray-200"
              }`}
            >
              <div
                className="tab-scroll flex border-b overflow-x-auto"
                style={{
                  scrollbarWidth: "thin",
                  scrollbarColor: isDarkMode
                    ? "#374151 #13161B"
                    : "#CBD5E1 #F1F5F9",
                }}
              >
                <style>{`
                  .tab-scroll::-webkit-scrollbar {
                    height: 3px;
                  }
                  .tab-scroll::-webkit-scrollbar-track {
                    background: ${isDarkMode ? "#13161B" : "#F1F5F9"};
                    border-radius: 0 0 8px 8px;
                  }
                  .tab-scroll::-webkit-scrollbar-thumb {
                    background: ${isDarkMode ? "#374151" : "#CBD5E1"};
                    border-radius: 4px;
                  }
                `}</style>
                {[
                  { id: "details", label: "Details", icon: FileText },
                  { id: "items", label: "Line Items", icon: Package },
                  { id: "terms", label: "Terms", icon: FileText },
                  { id: "tracking", label: "Tracking", icon: Eye },
                  { id: "signature", label: "Signature", icon: CheckCircle },
                ].map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex-shrink-0 px-4 sm:px-6 py-3 font-medium transition-colors ${
                        activeTab === tab.id
                          ? "text-blue-400 border-b-2 border-blue-400"
                          : isDarkMode
                            ? "text-gray-400 hover:text-white"
                            : "text-gray-500 hover:text-gray-900"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <Icon size={16} />
                        {tab.label}
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="p-4 sm:p-6">
                {/* Details Tab */}
                {activeTab === "details" && (
                  <div className="space-y-6">
                    <div>
                      <h3
                        className={`text-lg font-semibold mb-4 flex items-center gap-2 ${
                          isDarkMode ? "text-white" : "text-gray-900"
                        }`}
                      >
                        <FileText size={18} />
                        Quote Information
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <EditableField
                          label="Title"
                          field="title"
                          value={quote.title}
                          readOnly={isLocked}
                        />
                        <EditableField
                          label="Quote Number"
                          field="quoteNumber"
                          value={quote.quoteNumber}
                          readOnly
                        />
                        <EditableField
                          label="Status"
                          field="status"
                          value={STATUS_CONFIG[quote.status]?.label}
                          readOnly
                        />
                        <EditableField
                          label="Currency"
                          field="currency"
                          value={quote.currency}
                          readOnly
                        />
                        <EditableField
                          label="Valid Until"
                          field="validUntil"
                          value={formatDate(quote.validUntil)}
                          type="date"
                          readOnly={isLocked}
                        />
                        <EditableField
                          label="Sent Date"
                          field="sentAt"
                          value={
                            quote.sentAt
                              ? new Date(quote.sentAt).toLocaleString()
                              : "—"
                          }
                          readOnly
                        />
                        <div className="sm:col-span-2">
                          <EditableField
                            label="Description"
                            field="description"
                            value={quote.description}
                            type="textarea"
                            readOnly={isLocked}
                          />
                        </div>
                        <div className="sm:col-span-2">
                          <EditableField
                            label="Internal Notes"
                            field="notes"
                            value={quote.notes}
                            type="textarea"
                            readOnly={isLocked}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Customer */}
                    <div
                      className={`border-t pt-6 ${
                        isDarkMode ? "border-gray-700" : "border-gray-200"
                      }`}
                    >
                      <h3
                        className={`text-lg font-semibold mb-4 flex items-center gap-2 ${
                          isDarkMode ? "text-white" : "text-gray-900"
                        }`}
                      >
                        <Building2 size={18} />
                        Customer
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <EditableField
                          label="Customer Name"
                          field="customerName"
                          value={quote.customerName}
                          readOnly={isLocked}
                        />
                        <EditableField
                          label="Customer Email"
                          field="customerEmail"
                          value={quote.customerEmail}
                          type="email"
                          readOnly={isLocked}
                        />
                        <EditableField
                          label="Customer Company"
                          field="customerCompany"
                          value={quote.customerCompany}
                          readOnly={isLocked}
                        />
                        <EditableField
                          label="Customer Phone"
                          field="customerPhone"
                          value={quote.customerPhone}
                          type="tel"
                          readOnly={isLocked}
                        />
                      </div>
                    </div>

                    {/* System Info */}
                    <div
                      className={`border-t pt-6 ${
                        isDarkMode ? "border-gray-700" : "border-gray-200"
                      }`}
                    >
                      <h3
                        className={`text-lg font-semibold mb-4 ${
                          isDarkMode ? "text-white" : "text-gray-900"
                        }`}
                      >
                        System Information
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                        <div>
                          <label
                            className={
                              isDarkMode ? "text-gray-400" : "text-gray-500"
                            }
                          >
                            Created By
                          </label>
                          <p
                            className={`flex items-center gap-2 ${
                              isDarkMode ? "text-white" : "text-gray-900"
                            }`}
                          >
                            <User size={16} />
                            {quote.creator?.name || "—"}
                          </p>
                        </div>
                        <div>
                          <label
                            className={
                              isDarkMode ? "text-gray-400" : "text-gray-500"
                            }
                          >
                            Created Date
                          </label>
                          <p
                            className={
                              isDarkMode ? "text-white" : "text-gray-900"
                            }
                          >
                            {new Date(quote.createdAt).toLocaleString()}
                          </p>
                        </div>
                        <div>
                          <label
                            className={
                              isDarkMode ? "text-gray-400" : "text-gray-500"
                            }
                          >
                            Sent By
                          </label>
                          <p
                            className={`flex items-center gap-2 ${
                              isDarkMode ? "text-white" : "text-gray-900"
                            }`}
                          >
                            <User size={16} />
                            {quote.sender?.name || "—"}
                          </p>
                        </div>
                        <div>
                          <label
                            className={
                              isDarkMode ? "text-gray-400" : "text-gray-500"
                            }
                          >
                            Last Modified
                          </label>
                          <p
                            className={
                              isDarkMode ? "text-white" : "text-gray-900"
                            }
                          >
                            {new Date(quote.updatedAt).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Line Items Tab */}
                {activeTab === "items" && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3
                        className={`text-lg font-semibold ${
                          isDarkMode ? "text-white" : "text-gray-900"
                        }`}
                      >
                        Line Items ({quote.lineItems?.length || 0})
                      </h3>
                    </div>

                    {quote.lineItems?.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr
                              className={`border-b ${
                                isDarkMode
                                  ? "border-gray-700"
                                  : "border-gray-200"
                              }`}
                            >
                              {[
                                "Item",
                                "Qty",
                                "Unit Price",
                                "Tax",
                                "Total",
                              ].map((h) => (
                                <th
                                  key={h}
                                  className={`text-left py-2 px-2 font-medium ${
                                    isDarkMode
                                      ? "text-gray-400"
                                      : "text-gray-500"
                                  } ${
                                    h === "Total" || h === "Qty"
                                      ? "text-right"
                                      : ""
                                  }`}
                                >
                                  {h}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {quote.lineItems.map((li) => (
                              <tr
                                key={li.id}
                                className={`border-b ${
                                  isDarkMode
                                    ? "border-gray-800"
                                    : "border-gray-100"
                                }`}
                              >
                                <td className="py-3 px-2">
                                  <div
                                    className={`font-medium ${
                                      isDarkMode
                                        ? "text-white"
                                        : "text-gray-900"
                                    }`}
                                  >
                                    {li.name}
                                  </div>
                                  {li.description && (
                                    <div
                                      className={`text-xs mt-1 ${
                                        isDarkMode
                                          ? "text-gray-400"
                                          : "text-gray-500"
                                      }`}
                                    >
                                      {li.description}
                                    </div>
                                  )}
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {li.billingCycle !== "one_time" && (
                                      <span className="inline-block px-2 py-0.5 text-xs rounded bg-blue-500/10 text-blue-400 capitalize">
                                        {li.billingCycle.replace("_", " ")}
                                      </span>
                                    )}
                                    {li.termMonths && (
                                      <span className="inline-block px-2 py-0.5 text-xs rounded bg-purple-500/10 text-purple-400 border border-purple-500/20">
                                        {li.termMonths}-month term
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td
                                  className={`py-3 px-2 text-right ${
                                    isDarkMode ? "text-white" : "text-gray-900"
                                  }`}
                                >
                                  {li.quantity}
                                </td>
                                <td
                                  className={`py-3 px-2 ${
                                    isDarkMode ? "text-white" : "text-gray-900"
                                  }`}
                                >
                                  {formatCurrency(li.unitPrice, quote.currency)}
                                </td>
                                <td
                                  className={`py-3 px-2 ${
                                    isDarkMode
                                      ? "text-gray-400"
                                      : "text-gray-500"
                                  }`}
                                >
                                  {parseFloat(li.taxRate)}%
                                </td>
                                <td className="py-3 px-2 text-right font-semibold text-green-400">
                                  {formatCurrency(
                                    li.totalPrice,
                                    quote.currency,
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>

                        {/* Totals */}
                        <div className="flex justify-end mt-4">
                          <div
                            className={`w-full sm:w-80 rounded-lg p-4 space-y-2 ${
                              isDarkMode ? "bg-gray-800/50" : "bg-gray-100"
                            }`}
                          >
                            <div className="flex justify-between text-sm">
                              <span
                                className={
                                  isDarkMode ? "text-gray-400" : "text-gray-500"
                                }
                              >
                                Subtotal
                              </span>
                              <span
                                className={
                                  isDarkMode ? "text-white" : "text-gray-900"
                                }
                              >
                                {formatCurrency(quote.subtotal, quote.currency)}
                              </span>
                            </div>
                            {parseFloat(quote.discountAmount) > 0 && (
                              <div className="flex justify-between text-sm text-orange-400">
                                <span>Discount</span>
                                <span>
                                  -
                                  {quote.discountType === "percentage"
                                    ? `${quote.discountAmount}%`
                                    : formatCurrency(
                                        quote.discountAmount,
                                        quote.currency,
                                      )}
                                </span>
                              </div>
                            )}
                            <div className="flex justify-between text-sm">
                              <span
                                className={
                                  isDarkMode ? "text-gray-400" : "text-gray-500"
                                }
                              >
                                Tax
                              </span>
                              <span
                                className={
                                  isDarkMode ? "text-white" : "text-gray-900"
                                }
                              >
                                {formatCurrency(
                                  quote.taxAmount,
                                  quote.currency,
                                )}
                              </span>
                            </div>
                            <div
                              className={`flex justify-between text-base font-bold pt-2 border-t ${
                                isDarkMode
                                  ? "border-gray-700 text-white"
                                  : "border-gray-300 text-gray-900"
                              }`}
                            >
                              <span>Total</span>
                              <span className="text-green-400">
                                {formatCurrency(
                                  quote.totalAmount,
                                  quote.currency,
                                )}
                              </span>
                            </div>
                            {parseFloat(quote.monthlyRecurring) > 0 && (
                              <div
                                className={`text-xs text-right pt-1 ${
                                  isDarkMode ? "text-gray-400" : "text-gray-500"
                                }`}
                              >
                                +
                                {formatCurrency(
                                  quote.monthlyRecurring,
                                  quote.currency,
                                )}
                                /month recurring
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div
                        className={`text-center py-12 rounded-lg border ${
                          isDarkMode
                            ? "text-gray-400 bg-[#13161B] border-gray-700"
                            : "text-gray-500 bg-white border-gray-200"
                        }`}
                      >
                        <Package
                          size={48}
                          className="mx-auto mb-3 opacity-50"
                        />
                        <p>No line items</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Terms Tab */}
                {activeTab === "terms" && (
                  <div className="space-y-6">
                    <div>
                      <h3
                        className={`text-lg font-semibold mb-4 flex items-center gap-2 ${
                          isDarkMode ? "text-white" : "text-gray-900"
                        }`}
                      >
                        <FileText size={18} />
                        Terms &amp; Conditions
                      </h3>
                      <EditableField
                        label="Payment Terms"
                        field="paymentTerms"
                        value={quote.paymentTerms}
                        readOnly={isLocked}
                      />
                      <div className="mt-4">
                        <EditableField
                          label="Terms &amp; Conditions"
                          field="termsAndConditions"
                          value={quote.termsAndConditions}
                          type="textarea"
                          readOnly={isLocked}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Tracking Tab */}
                {activeTab === "tracking" && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div
                        className={`rounded-lg p-4 ${
                          isDarkMode ? "bg-gray-800/30" : "bg-gray-100"
                        }`}
                      >
                        <div
                          className={`text-xs uppercase mb-1 ${
                            isDarkMode ? "text-gray-400" : "text-gray-500"
                          }`}
                        >
                          Total Views
                        </div>
                        <div className="text-2xl font-bold text-blue-400">
                          {quote.viewCount || 0}
                        </div>
                      </div>
                      <div
                        className={`rounded-lg p-4 ${
                          isDarkMode ? "bg-gray-800/30" : "bg-gray-100"
                        }`}
                      >
                        <div
                          className={`text-xs uppercase mb-1 ${
                            isDarkMode ? "text-gray-400" : "text-gray-500"
                          }`}
                        >
                          First Viewed
                        </div>
                        <div
                          className={`text-sm font-semibold ${
                            isDarkMode ? "text-white" : "text-gray-900"
                          }`}
                        >
                          {quote.firstViewedAt
                            ? new Date(quote.firstViewedAt).toLocaleString()
                            : "Not yet viewed"}
                        </div>
                      </div>
                      <div
                        className={`rounded-lg p-4 ${
                          isDarkMode ? "bg-gray-800/30" : "bg-gray-100"
                        }`}
                      >
                        <div
                          className={`text-xs uppercase mb-1 ${
                            isDarkMode ? "text-gray-400" : "text-gray-500"
                          }`}
                        >
                          Last Viewed
                        </div>
                        <div
                          className={`text-sm font-semibold ${
                            isDarkMode ? "text-white" : "text-gray-900"
                          }`}
                        >
                          {quote.lastViewedAt
                            ? new Date(quote.lastViewedAt).toLocaleString()
                            : "—"}
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3
                        className={`text-lg font-semibold mb-4 flex items-center gap-2 ${
                          isDarkMode ? "text-white" : "text-gray-900"
                        }`}
                      >
                        <ActivityIcon size={18} />
                        View History
                      </h3>

                      {quote.views?.length > 0 ? (
                        <div className="space-y-2">
                          {quote.views.map((view) => (
                            <div
                              key={view.id}
                              className={`rounded-lg p-3 flex items-start gap-3 ${
                                isDarkMode ? "bg-gray-800/30" : "bg-gray-100"
                              }`}
                            >
                              <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                                <Eye size={14} className="text-blue-400" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div
                                  className={`text-sm font-medium ${
                                    isDarkMode ? "text-white" : "text-gray-900"
                                  }`}
                                >
                                  {new Date(view.viewedAt).toLocaleString()}
                                </div>
                                <div
                                  className={`text-xs flex flex-wrap gap-2 mt-1 ${
                                    isDarkMode
                                      ? "text-gray-400"
                                      : "text-gray-500"
                                  }`}
                                >
                                  {view.ipAddress && (
                                    <span className="flex items-center gap-1">
                                      <Globe size={10} />
                                      {view.ipAddress}
                                    </span>
                                  )}
                                  {view.sessionDuration && (
                                    <span className="flex items-center gap-1">
                                      <Clock size={10} />
                                      {view.sessionDuration}s on page
                                    </span>
                                  )}
                                  {view.userAgent && (
                                    <span
                                      className="truncate max-w-xs"
                                      title={view.userAgent}
                                    >
                                      {view.userAgent.substring(0, 60)}
                                      {view.userAgent.length > 60 && "..."}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div
                          className={`text-center py-8 rounded-lg border ${
                            isDarkMode
                              ? "text-gray-400 bg-[#13161B] border-gray-700"
                              : "text-gray-500 bg-white border-gray-200"
                          }`}
                        >
                          <Eye size={32} className="mx-auto mb-2 opacity-50" />
                          <p className="text-sm">No views recorded yet</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Signature Tab */}
                {activeTab === "signature" && (
                  <div>
                    <h3
                      className={`text-lg font-semibold mb-4 flex items-center gap-2 ${
                        isDarkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      <CheckCircle size={18} />
                      Digital Signature
                    </h3>

                    {quote.signature ? (
                      <div className="space-y-4">
                        <div
                          className={`rounded-lg border p-4 ${
                            isDarkMode
                              ? "bg-green-500/5 border-green-500/30"
                              : "bg-green-50 border-green-200"
                          }`}
                        >
                          <div className="flex items-center gap-2 mb-3">
                            <CheckCircle size={20} className="text-green-400" />
                            <span className="font-semibold text-green-400">
                              Signed &amp; Accepted
                            </span>
                          </div>
                          <div
                            className={`p-4 rounded-lg ${
                              isDarkMode ? "bg-white" : "bg-white"
                            } border border-gray-200`}
                          >
                            <img
                              src={quote.signature.signatureImage}
                              alt="Signature"
                              className="max-h-32 mx-auto"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <EditableField
                            label="Signer Name"
                            field="signerName"
                            value={quote.signature.signerName}
                            readOnly
                          />
                          <EditableField
                            label="Signer Email"
                            field="signerEmail"
                            value={quote.signature.signerEmail}
                            readOnly
                          />
                          <EditableField
                            label="Signer Title"
                            field="signerTitle"
                            value={quote.signature.signerTitle}
                            readOnly
                          />
                          <EditableField
                            label="Signer Company"
                            field="signerCompany"
                            value={quote.signature.signerCompany}
                            readOnly
                          />
                          <EditableField
                            label="Signed At"
                            field="signedAt"
                            value={new Date(
                              quote.signature.signedAt,
                            ).toLocaleString()}
                            readOnly
                          />
                          <EditableField
                            label="Signature Method"
                            field="signatureMethod"
                            value={
                              quote.signature.signatureMethod === "drawn"
                                ? "Drawn"
                                : "Typed"
                            }
                            readOnly
                          />
                          <EditableField
                            label="IP Address"
                            field="ipAddress"
                            value={quote.signature.ipAddress}
                            readOnly
                          />
                          <EditableField
                            label="Consent"
                            field="consentAccepted"
                            value={
                              quote.signature.consentAccepted
                                ? "Accepted"
                                : "Not accepted"
                            }
                            readOnly
                          />
                          {quote.signature.userAgent && (
                            <div className="sm:col-span-2">
                              <EditableField
                                label="User Agent"
                                field="userAgent"
                                value={quote.signature.userAgent}
                                readOnly
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div
                        className={`text-center py-12 rounded-lg border ${
                          isDarkMode
                            ? "text-gray-400 bg-[#13161B] border-gray-700"
                            : "text-gray-500 bg-white border-gray-200"
                        }`}
                      >
                        <CheckCircle
                          size={48}
                          className="mx-auto mb-3 opacity-50"
                        />
                        <p className="text-lg mb-2">Not yet signed</p>
                        <p className="text-sm">
                          The customer hasn't signed this quote yet
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-6">
            {/* Quick Stats */}
            <div
              className={`rounded-lg border p-4 ${
                isDarkMode
                  ? "bg-[#13161B] border-gray-700"
                  : "bg-white border-gray-200"
              }`}
            >
              <h3
                className={`font-semibold mb-4 ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Quote Summary
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span
                    className={`text-sm ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Subtotal
                  </span>
                  <span
                    className={`font-medium ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    {formatCurrency(quote.subtotal, quote.currency)}
                  </span>
                </div>
                {parseFloat(quote.discountAmount) > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-orange-400">Discount</span>
                    <span className="font-medium text-orange-400">
                      -
                      {quote.discountType === "percentage"
                        ? `${quote.discountAmount}%`
                        : formatCurrency(quote.discountAmount, quote.currency)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <span
                    className={`text-sm ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Tax
                  </span>
                  <span
                    className={`font-medium ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    {formatCurrency(quote.taxAmount, quote.currency)}
                  </span>
                </div>
                <div
                  className={`flex justify-between items-center pt-3 border-t ${
                    isDarkMode ? "border-gray-700" : "border-gray-200"
                  }`}
                >
                  <span
                    className={`font-semibold ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    Total
                  </span>
                  <span className="font-bold text-lg text-green-400">
                    {formatCurrency(quote.totalAmount, quote.currency)}
                  </span>
                </div>
                {parseFloat(quote.monthlyRecurring) > 0 && (
                  <div
                    className={`flex justify-between items-center text-xs pt-1 ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    <span>Monthly recurring</span>
                    <span>
                      {formatCurrency(quote.monthlyRecurring, quote.currency)}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Public Link */}
            <div
              className={`rounded-lg border p-4 ${
                isDarkMode
                  ? "bg-[#13161B] border-gray-700"
                  : "bg-white border-gray-200"
              }`}
            >
              <h3
                className={`font-semibold mb-2 flex items-center gap-2 ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                <LinkIcon size={16} />
                Customer Link
              </h3>
              <p
                className={`text-xs mb-3 ${
                  isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                Shareable link for the customer to view and sign this quote
              </p>
              <div
                className={`p-2 rounded text-xs font-mono break-all mb-3 ${
                  isDarkMode
                    ? "bg-gray-800 text-gray-300"
                    : "bg-gray-100 text-gray-700"
                }`}
              >
                {publicUrl}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleCopyPublicLink}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm transition-colors flex items-center justify-center gap-2 ${
                    isDarkMode
                      ? "bg-gray-700 hover:bg-gray-600"
                      : "bg-gray-200 hover:bg-gray-300"
                  }`}
                >
                  {copied ? (
                    <>
                      <CheckCircle size={14} className="text-green-400" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy size={14} />
                      Copy
                    </>
                  )}
                </button>
                <button
                  onClick={() => window.open(publicUrl, "_blank")}
                  className="flex-1 px-3 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm transition-colors flex items-center justify-center gap-2"
                >
                  <ExternalLink size={14} />
                  Open
                </button>
              </div>
            </div>

            {/* Linked Records */}
            {(quote.opportunity || quote.account || quote.contact) && (
              <div
                className={`rounded-lg border p-4 ${
                  isDarkMode
                    ? "bg-[#13161B] border-gray-700"
                    : "bg-white border-gray-200"
                }`}
              >
                <h3
                  className={`font-semibold mb-4 ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  Linked Records
                </h3>
                <div className="space-y-3">
                  {quote.opportunity && (
                    <div>
                      <label
                        className={`text-xs ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        Opportunity
                      </label>
                      <button
                        onClick={() =>
                          navigate(`/opportunities/${quote.opportunity.id}`)
                        }
                        className="text-blue-400 hover:text-blue-300 font-medium flex items-center gap-1 text-sm"
                      >
                        <Target size={14} />
                        {quote.opportunity.name}
                        <ExternalLink size={12} />
                      </button>
                    </div>
                  )}
                  {quote.account && (
                    <div>
                      <label
                        className={`text-xs ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        Account
                      </label>
                      <button
                        onClick={() =>
                          navigate(`/accounts/${quote.account.id}`)
                        }
                        className="text-blue-400 hover:text-blue-300 font-medium flex items-center gap-1 text-sm"
                      >
                        <Building2 size={14} />
                        {quote.account.name}
                        <ExternalLink size={12} />
                      </button>
                    </div>
                  )}
                  {quote.contact && (
                    <div>
                      <label
                        className={`text-xs ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        Contact
                      </label>
                      <button
                        onClick={() =>
                          navigate(`/contacts/${quote.contact.id}`)
                        }
                        className="text-blue-400 hover:text-blue-300 font-medium flex items-center gap-1 text-sm"
                      >
                        <User size={14} />
                        {quote.contact.firstName} {quote.contact.lastName}
                        <ExternalLink size={12} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuoteDetail;
